import React, { useState, useContext, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import ReferralLinkModal from './ReferralLinkModal';
import removeLocalStorage from './RemoveLocalStorage';
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton';
import { store } from 'components/Store';
import locationSearchDescriptor from 'components/Dashboard/helpers/locationSearchDescriptor';
import parliamentarianUsers, { dataHubUsers } from 'components/Dashboard/SidebarElements/parliamentarianUsers';
import useTopicOrClientWord from 'components/Dashboard/hooks/useTopicOrClientWord';
import normalize from 'components/Dashboard/utilities/normalizeString';
import idsWithoutImages from 'components/Dashboard/KeyUpdates/idsWithoutImages';
import { Portal } from 'react-portal';
import useIsMobile from 'components/Dashboard/utilities/useIsMobile';
import ClientsPicker from './ClientsPicker';
import getAppSite, { currencySymbol } from 'utils/getAppSite';
import useLogoUrlReplacement from 'components/Dashboard/UserContent/ui/useLogoUrlReplacement';
import DefaultPageSelector from 'components/Dashboard/Navigation/DefaultPageSelector';
import CustomScrollbar from 'components/Common/CustomScrollbar';
import { useHeightContainer } from 'components/Dashboard/utilities/useHeightContainer';
import { unregisterServiceWorker } from 'components/Settings/EmailAlerts/pushServiceWorkerUtils';

const ProfileButton = () => {
  const globalState = useContext(store);
  const { state } = globalState;
  const { activePlan, team } = state;
  const { code, subscriptionStatus } = activePlan;
  const location = useLocation();
  const returnUrl = `?returnUrl=${encodeURIComponent(
    `${
      location?.pathname === '/reports/create'
        ? '/reports'
        : `${location.pathname}${locationSearchDescriptor()}${location.hash}`
    }`
  )}`;

  const [isShowSettingsMenu, setIsShowSettingsMenu] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const [showReferralLinkModal, setShowReferralLinkModal] = useState(false);

  useEffect(() => {
    if (Object.keys(activePlan).length > 0) {
      setIsLoading(false);
    }
  }, [activePlan]);

  const { user, logout } = useAuth0();
  const logoutWithRedirect = () => logout({ logoutParams: { returnTo: window.location.origin } });

  const isParliamentarian = parliamentarianUsers(code);
  const dataHubUser = dataHubUsers(activePlan);
  const { transformTopicOrClientWord, newAgencyPlan } = useTopicOrClientWord();
  const trialCondition = subscriptionStatus === 'InTrial' || subscriptionStatus === 'TrialEnded';
  const profileElement = useRef(null);

  const { top, left, width, height } = profileElement?.current?.getBoundingClientRect() ?? {};
  const isMobile = useIsMobile();
  const menuWidth = newAgencyPlan ? 230 : 220;
  const { replacementOfUrl } = useLogoUrlReplacement();
  const [heightContainer, containerRef] = useHeightContainer();
  const [organisationLogoState, setOrganisationLogoState] = useState({ status: 'loading', src: null });

  useEffect(() => {
    const logo = newAgencyPlan ? (activePlan?.organisationLogo ?? user.picture) : team?.organisationLogo;
    if (!logo) {
      setOrganisationLogoState({ status: 'failed', src: null });
      return;
    }
    const resolvedUrl = replacementOfUrl({ url: logo }) ?? logo;
    const img = new Image();
    img.onload = () => setOrganisationLogoState({ status: 'loaded', src: resolvedUrl });
    img.onerror = () => setOrganisationLogoState({ status: 'failed', src: null });
    img.src = resolvedUrl;
  }, [team?.organisationLogo]);

  const createUrl = () => {
    let url;
    if (newAgencyPlan && isMobile) {
      url = team?.organisationLogo;
    } else {
      url = activePlan?.organisationLogo ?? user.picture;
    }
    return replacementOfUrl({ url });
  };

  return (
    <div className='position-relative' ref={profileElement}>
      {isLoading && (
        <SkeletonTheme color='#f2f2f2'>
          <Skeleton height={33} width={69} style={{ borderRadius: '20px' }} />
        </SkeletonTheme>
      )}
      {!isLoading && (
        <button
          className={`nav-user-profile general-button ${isShowSettingsMenu ? 'active' : ''}`}
          onClick={() => {
            setIsShowSettingsMenu(!isShowSettingsMenu);
          }}
        >
          <span className='hamburger-box'>
            <span className='hamburger-inner'></span>
          </span>
          <span
            className='profile-image'
            style={{
              backgroundImage: `url(${createUrl()})`,
            }}
          />
        </button>
      )}
      {isShowSettingsMenu && (
        <>
          <Portal node={document && document.querySelector('body')}>
            <div
              className='general-close-background adjustment-of-position'
              onClick={() => setIsShowSettingsMenu(false)}
            />
            <nav
              className='login-navbar adjustment-of-position'
              ref={containerRef}
              style={{
                position: 'fixed',
                top: `${top + height + 10}px`,
                left: `${left + width - menuWidth}px`,
                width: `${menuWidth}px`,
              }}
            >
              <CustomScrollbar
                autoHeight
                autoHeightMin='100%'
                autoHeightMax={`${heightContainer}px`}
                className='simple-scrollbar'
              >
                <div className='py-2'>
                  {!dataHubUser && (
                    <>
                      <DefaultPageSelector />
                      <hr className='my-1' />
                    </>
                  )}
                  {parliamentarianUsers(code) && team?.parliamentarianId !== null && (
                    <>
                      <a
                        className='settings-button setting-item loginbar-profile-button d-flex'
                        rel='noreferrer'
                        href={`/stakeholders/${team?.parliamentarianId}/${normalize(
                          team?.parliamentarianOfficialName
                        )}?topic-id=all`}
                        target='_blank'
                      >
                        <img
                          className=''
                          alt='stakeholder profile'
                          src={
                            !!team?.parliamentarianImageId && !idsWithoutImages.includes(team?.parliamentarianImageId)
                              ? `https://www.parliamenttoday.com/image.php?imageid=${team?.parliamentarianImageId}&ml=1`
                              : `${import.meta.env.VITE_APP_CDNURL}/images/20210722-stakeholders-placeholder.png`
                          }
                        />
                        Public profile
                      </a>
                      <hr className='my-1' />
                    </>
                  )}

                  {newAgencyPlan && (
                    <>
                      <div className='d-lg-none navigation-client-picker'>
                        <ClientsPicker
                          dropdownAdditionalClass={'adjustment-of-position'}
                          additionalClass={' client-picker-complete mx-0'}
                          notFixed={true}
                        />
                      </div>
                      <Link className='settings-button setting-item d-flex' to={`/settings/clients${returnUrl}`}>
                        <span className='icon-container-clients'>
                          <span className='political-updates-nav-icon icon-clients' />
                        </span>
                        <span>Clients and prospects</span>
                      </Link>
                    </>
                  )}

                  {!newAgencyPlan && (
                    <Link
                      className='settings-button setting-item d-flex align-items-center'
                      to={`/settings/organisation-description${returnUrl}`}
                    >
                      <span className='icon-container-text-edit'>
                        <span className='political-updates-nav-icon icon-text-edit' />
                      </span>
                      <span>Description</span>
                    </Link>
                  )}

                  <Link className='settings-button setting-item d-flex' to={`/settings/team${returnUrl}`}>
                    <span className='icon-container-team'>
                      <span className='political-updates-nav-icon icon-team' />
                    </span>
                    <span>Team</span>
                  </Link>

                  {!newAgencyPlan && (
                    <Link
                      className='settings-button setting-item d-flex'
                      to={`/settings/${transformTopicOrClientWord({
                        plural: true,
                      })}${returnUrl}`}
                    >
                      <span className='icon-container-topics'>
                        <span className='political-updates-nav-icon icon-topics' />
                      </span>
                      <span>{transformTopicOrClientWord({ plural: true, uppercase: true })}</span>
                    </Link>
                  )}

                  {!dataHubUser && (
                    <>
                      <Link className='settings-button setting-item d-flex' to={`/settings/alerts${returnUrl}`}>
                        <span className='icon-container-alert-medium'>
                          <span className='political-updates-nav-icon icon-alert-medium' />
                        </span>
                        <span>Alerts</span>
                      </Link>
                    </>
                  )}

                  <Link className='settings-button setting-item d-flex' to={`/settings/integrations${returnUrl}`}>
                    <span className='icon-container-associated-content'>
                      <span className='political-updates-nav-icon icon-associated-content' />
                    </span>
                    <span>Integrations</span>
                  </Link>

                  <Link
                    className='settings-button setting-item d-flex'
                    to={`/saved-items`}
                    onClick={() => setIsShowSettingsMenu(false)}
                  >
                    <span className='icon-container-saved-items'>
                      <span className='political-updates-nav-icon icon-saved-items' />
                    </span>
                    <span>Saved items</span>
                  </Link>

                  <Link
                    className='settings-button setting-item d-flex'
                    to={`/upload-hub`}
                    onClick={() => setIsShowSettingsMenu(false)}
                  >
                    <span className='icon-container-upload-hub'>
                      <span className='political-updates-nav-icon icon-upload' />
                    </span>
                    <span>Upload Hub</span>
                  </Link>

                  <Link
                    className='settings-button setting-item d-flex'
                    onClick={() => setIsShowSettingsMenu(false)}
                    to={`/notes/my-own`}
                  >
                    <span className='icon-container-edit-pencil'>
                      <span className='political-updates-nav-icon icon-edit-pencil' />
                    </span>
                    <span>Note log</span>
                  </Link>

                  <Link className='settings-button setting-item d-flex' to={`/settings/embeddables${returnUrl}`}>
                    <span className='icon-container-code'>
                      <span className='political-updates-nav-icon icon-code' />
                    </span>
                    <span>Embeddables</span>
                  </Link>

                  <Link
                    className='settings-button setting-item d-flex align-items-center'
                    to={`/settings/account${returnUrl}`}
                  >
                    {organisationLogoState.status === 'loaded' ? (
                      <div className='user-nav-organisation-logo-container flex-centered specific-right-margin-12 justify-content-center text-center'>
                        <img src={organisationLogoState.src} alt='' className='user-nav-organisation-logo' />
                      </div>
                    ) : (
                      <span className='icon-container-edit-rectangle'>
                        <span className='political-updates-nav-icon icon-edit-rectangle' />
                      </span>
                    )}
                    <span>All settings</span>
                  </Link>

                  <hr className='my-1' />
                  <a
                    className='settings-button setting-item d-flex'
                    href='https://support.policymogul.com'
                    onClick={(e) => {
                      e.preventDefault();
                      window.Intercom('startSurvey', 24505456);
                    }}
                  >
                    <span className='icon-container-idea'>
                      <span className='political-updates-nav-icon icon-idea' />
                    </span>
                    <span>Suggest a feature</span>
                  </a>
                  {isParliamentarian ? (
                    <button
                      className={`settings-button setting-item d-flex btn btn-default btn-refer ${
                        trialCondition ? '' : 'btn-refer-subscribed'
                      }`}
                      onClick={() => {
                        setIsShowSettingsMenu(false);
                        setShowReferralLinkModal(true);
                      }}
                    >
                      <span className='icon-container-logout'>
                        <span className='political-updates-nav-icon icon-share' />
                      </span>
                      <span>Share PolicyMogul</span>
                    </button>
                  ) : (
                    <>
                      {getAppSite() !== 'usa' && (
                        <button
                          className={`settings-button setting-item d-flex btn btn-default btn-refer ${
                            trialCondition ? '' : 'btn-refer-subscribed'
                          }`}
                          onClick={() => {
                            setIsShowSettingsMenu(false);
                            setShowReferralLinkModal(true);
                          }}
                        >
                          <span className='icon-container-logout'>
                            <span className='political-updates-nav-icon icon-present' />
                          </span>
                          <span>Refer and earn {currencySymbol}300</span>
                        </button>
                      )}
                    </>
                  )}
                  {trialCondition && !newAgencyPlan && (
                    <Link className='settings-button setting-item d-flex text-light-blue' to={`/pricing${returnUrl}`}>
                      <span className='icon-container-billing'>
                        <span className='political-updates-nav-icon icon-billing' />
                      </span>
                      <span>Choose your plan</span>
                    </Link>
                  )}
                  <hr className='my-1' />
                  <button
                    className='settings-button setting-item d-flex btn btn-default btn-logout'
                    onClick={() => {
                      removeLocalStorage();
                      logoutWithRedirect();
                      unregisterServiceWorker();
                    }}
                  >
                    <span className='icon-container-logout'>
                      <span className='political-updates-nav-icon icon-logout' />
                    </span>
                    <span>Logout</span>
                  </button>
                </div>
              </CustomScrollbar>
            </nav>
          </Portal>
        </>
      )}
      <Portal>
        <ReferralLinkModal
          setShowPopup={setShowReferralLinkModal}
          isParliamentarian={isParliamentarian}
          showPopUp={showReferralLinkModal}
        />
      </Portal>
    </div>
  );
};

export default ProfileButton;
